import React from 'react'
import classNames from 'classnames'
import { components } from 'react-select'
import pluralize from 'pluralize'

import { Checkbox } from '@/components/forms/Checkbox'

// Standard interaction colors for every dropdown surface: hover/focus is
// --color-background-strong, selected is --color-background-brand, and a
// selected option under the pointer deepens to --color-background-brand-hover
// so it still shows hover feedback.
// Portal menus to document.body so they escape ancestor overflow / transform
// clipping (modal content has overflow: auto, scrollable tables,
// the org settings bar). Disabled in tests because react-select-event v5
// looks for options inside the rendered container, not in document.body.
export const MENU_PORTAL_TARGET =
  process.env.NODE_ENV !== 'test' && typeof document !== 'undefined' ? document.body : undefined

function getBgColor(state) {
  if (state.isSelected) {
    return state.isFocused ? 'var(--color-background-brand-hover)' : 'var(--color-background-brand)'
  }

  if (state.isFocused) {
    return 'var(--color-background-hover)'
  }

  return 'var(--color-background-surface)'
}

// `autoWidth` drops the 190px floor and the fixed cap so the control is exactly
// as wide as what it's showing, and the menu exactly as wide as its longest
// option — for short, uniform labels (a timestamp, a version, a channel) that
// would otherwise sit in a control several times their width, or get cut off by
// the cap. Long or variable-length labels still want the default band. Single
// selects only: MultiSelect sizes itself around its chips (see multiSelectStyles).
//
// It takes more than a width on the container: react-select positions the value
// and the placeholder absolutely, so they contribute no intrinsic width and a
// content-sized control collapses to just its arrow. Both go back in flow here —
// the value container is already a centered flex row, so they still sit centered
// without the absolute positioning and its transform.
const inFlowLabel = (provided) => ({
  ...provided,
  position: 'static',
  transform: 'none',
  maxWidth: 'none',
})

export const selectStyles = (fixedWidth, error, autoWidth) => {
  return {
    container: (provided) => ({
      ...provided,
      ...(autoWidth
        ? { width: 'max-content', maxWidth: '100%' }
        : { minWidth: '190px', maxWidth: fixedWidth ? '300px' : '100%' }),
      borderRadius: 'var(--border-radius-button)',
    }),
    control: (provided, state) => ({
      ...provided,
      borderRadius: 'var(--border-radius-button)',
      borderWidth: error ? '2px' : '1px',
      borderColor: state.isDisabled
        ? 'var(--color-border)'
        : state.isFocused
          ? 'var(--color-brand)'
          : 'var(--color-border-strong)',
      boxShadow: state.isFocused ? `0 0 0 1px var(--color-brand)` : 'none',
      background: state.isDisabled
        ? 'var(--color-background-disabled)'
        : 'var(--color-background-surface)',
      ...(error && { borderColor: 'var(--color-critical)' }),
      '&:hover': {
        borderColor: state.isFocused ? 'var(--color-brand)' : 'var(--color-border-strong)',
      },
    }),
    dropdownIndicator: (provided, state) => ({
      ...provided,
      cursor: 'pointer',
      ...(state.isDisabled && { color: 'var(--color-icon-disabled)' }),
    }),
    clearIndicator: (provided) => ({
      ...provided,
      cursor: 'pointer',
    }),
    groupHeading: (provided) => ({
      ...provided,
      color: 'var(--color-text-secondary)',
      fontSize: '0.9rem',
    }),
    placeholder: (provided, state) => ({
      ...(autoWidth ? inFlowLabel(provided) : provided),
      color: state.isDisabled ? 'var(--color-text-disabled)' : 'var(--color-text-muted)',
    }),
    singleValue: (provided, state) => ({
      ...(autoWidth ? inFlowLabel(provided) : provided),
      color: state.isDisabled ? 'var(--color-text-disabled)' : 'var(--color-text)',
    }),
    menu: (provided) => ({
      ...provided,
      // max-content sizes the menu to its longest option; min-width keeps it at
      // least as wide as the control so it never looks detached from it.
      ...(autoWidth
        ? { width: 'max-content', minWidth: '100%' }
        : { width: fixedWidth ? '300px' : '100%' }),
      border: '1px solid var(--color-border)',
      boxShadow: 'var(--box-shadow-medium)',
      zIndex: 40,
    }),
    menuList: (provided) => ({
      ...provided,
      borderRadius: 'var(--border-radius-button)',
    }),
    option: (provided, state) => ({
      ...provided,
      cursor: 'pointer',
      fontSize: '1rem',
      fontWeight: state.isSelected ? 600 : 500,
      lineHeight: 1.2,
      padding: '0.5rem 0.75rem',
      background: getBgColor(state),
      color: 'var(--color-text)',
      ':active': {
        background: getBgColor(state),
      },
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
  }
}

export const multiSelectStyles = (
  fixedWidth,
  condenseOptions,
  showFullOptions,
  isSelectAll,
  error
) => {
  return {
    ...selectStyles(fixedWidth, error),
    valueContainer: (provided) => ({
      ...provided,
      flexWrap: showFullOptions ? 'wrap' : 'noWrap',
      padding: '2px 6px',
    }),
    option: (provided, state) => ({
      ...provided,
      alignItems: 'center',
      cursor: 'pointer',
      display: 'flex',
      fontSize: '1rem',
      fontWeight: state.isSelected ? 600 : 500,
      lineHeight: 1.2,
      padding: '.6rem .75rem',
      background: getBgColor(state),
      opacity: state.isDisabled ? 0.5 : 1,
      color: 'var(--color-text)',
      ':active': {
        background: getBgColor(state),
      },
    }),
    multiValue: (provided) => ({
      ...provided,
      background: 'var(--color-background-strong)',
      borderRadius: 'var(--border-radius-button)',
      fontSize: '1rem',
      fontWeight: 600,
      maxWidth: 'unset',
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: 'var(--color-text)',
      borderRadius: 'var(--border-radius-button)',
      fontSize: '100%',
      padding: '3px 6px',
    }),
    multiValueRemove: (provided) => {
      return condenseOptions ? { display: 'none' } : provided
    },
    menu: (provided) => ({
      ...provided,
      overflow: 'hidden',
      border: '1px solid var(--color-border)',
      boxShadow: 'var(--box-shadow-medium)',
      zIndex: 99,
      width: fixedWidth ? '300px' : '100%',
    }),
    menuList: (provided) => ({
      ...provided,
      ...(isSelectAll && { paddingTop: 0 }),
      borderRadius: 'var(--border-radius-button)',
    }),
  }
}
export const selectTheme = (theme) => ({
  ...theme,
  borderRadius: 8,
  colors: {
    ...theme.colors,
    neutral0: 'var(--color-background-surface)', // Menu and control background
    neutral20: 'var(--color-icon)', // Dropdown and clear indicators, indicator separator
    neutral40: 'var(--color-icon-hover)', // Hovered indicators
    neutral80: 'var(--color-text)', // Typed input text
    primary25: 'var(--color-background-hover)', // Hovered/focused option (fallback; selectStyles overrides)
    primary50: 'var(--color-background-hover)', // Mouse-down on an option — same as hover so clicking doesn't darken
    primary: 'var(--color-background-brand)', // Selected option (fallback; selectStyles overrides)
  },
})

// Default option renderer for all shared Selects: options may carry a
// `description` (string or node) that renders as a muted line under the label
// in the menu, matching SearchableDropdown's option descriptions. Consumers
// can still pass their own formatOptionLabel to override.
export const formatOptionWithDescription = (option, { context }) => {
  if (context === 'menu' && option.description) {
    return (
      <>
        <div>{option.label}</div>
        <div className="select-option-description">{option.description}</div>
      </>
    )
  }

  return option.label
}

export const MenuList = (props) => {
  if (!props.selectProps.isSelectAll) {
    return <components.MenuList {...props}>{props.children}</components.MenuList>
  }

  let optionsLength = 0
  if (props.children.length) {
    props.children.forEach((item) => {
      optionsLength += item.options ? item.options.length : 1
    })
  } else if (props.selectProps.inputValue === '') {
    optionsLength = props.selectProps.optionsLength ?? 0
  }
  const itemCount = pluralize(props.selectProps.label ?? 'item', optionsLength, true)

  return (
    <components.MenuList {...props}>
      <div className="show-options-header">{itemCount}</div>
      {props.children}
    </components.MenuList>
  )
}

export const Option = (props) => {
  const { innerProps, selectProps, data, isFocused, ...otherProps } = props
  // Keep isFocused so the shared option styles (getBgColor) paint the standard
  // hover/selected backgrounds — the .option-component classes carry no colors.
  const newProps = { innerProps: { ...innerProps }, isFocused, ...otherProps }
  const { formatOptionLabel } = selectProps

  return (
    <components.Option
      {...newProps}
      className={classNames(
        newProps.isDisabled ? 'option-component-disabled' : 'option-component',
        {
          'option-component--focused': isFocused,
        }
      )}
    >
      {/* Decoration: react-select's own option row owns the click and exposes aria-selected. */}
      <Checkbox
        checked={props.isSelected}
        aria-hidden="true"
        tabIndex={-1}
        style={{ marginRight: '1rem', pointerEvents: 'none' }}
      />
      <div>
        {formatOptionLabel
          ? formatOptionLabel(data, {
              context: 'menu',
              inputValue: selectProps.inputValue,
              selectValue: selectProps.value,
            })
          : props.label}
      </div>
    </components.Option>
  )
}

export const MultiValue = (props) => {
  if (props.index > 0) return null
  const { length } = props.getValue()

  if (props.selectProps.allowSelectAll) {
    const { options = [] } = props.options?.[0] ?? {}
    if (options.length === length) {
      return <div className="option-label">All</div>
    }
  }

  return length > 1 ? (
    <div className="option-label">{`${length} selected`}</div>
  ) : (
    <components.MultiValue {...props} />
  )
}

export const Input = (props) => {
  if (props.isHidden) {
    return <components.Input {...props} />
  }
  return <components.Input {...props} type="number" className="creatable-number-field" />
}
